import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentUser, AuthUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { updateUserProfile } from './api';
import AuthPage from './components/AuthPage';
import Nav from './components/Nav';
import Home from './pages/Home';
import Matches from './pages/Matches';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import MyPicks from './pages/MyPicks';
import ThemeShowcase from './pages/ThemeShowcase';
import Admin from './pages/Admin';
import FeedbackButton from './components/FeedbackButton';

type Pick = 'home' | 'draw' | 'away';

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [displayName, setDisplayName] = useState<string>(
    () => localStorage.getItem('topqik_display_name') ?? ''
  );
  const [picks, setPicks] = useState<Record<string, Pick>>({});

  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setChecking(false));

    return Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        getCurrentUser().then(async (u) => {
          setUser(u);
          const pending = localStorage.getItem('topqik_pending_wc_picks');
          if (pending) {
            try { await updateUserProfile(JSON.parse(pending)); } catch {}
            localStorage.removeItem('topqik_pending_wc_picks');
          }
        }).catch(() => setUser(null));
      }
      if (payload.event === 'signedOut') {
        setUser(null);
        setPicks({});
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    try {
      const stored = JSON.parse(
        localStorage.getItem(`topqik_picks_${user.userId}`) ?? '{}'
      );
      setPicks(stored);
    } catch {
      setPicks({});
    }
  }, [user?.userId]);

  function handleDisplayNameChange(name: string) {
    setDisplayName(name);
    localStorage.setItem('topqik_display_name', name);
  }

  function handlePick(matchId: string, pick: Pick) {
    if (!user) return;
    setPicks(prev => {
      const next = { ...prev, [matchId]: pick };
      localStorage.setItem(`topqik_picks_${user.userId}`, JSON.stringify(next));
      return next;
    });
  }

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 36, height: 36, border: '3px solid var(--border)', borderTopColor: 'var(--accent-amber)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const defaultName = user
    ? (user.signInDetails?.loginId ?? 'Player').split('@')[0]
    : '';
  const resolvedName = displayName || defaultName;
  const pickCount = Object.keys(picks).length;
  const isAdmin = user?.signInDetails?.loginId === 'swapnilp1994@gmail.com';

  return (
    <BrowserRouter>
      <Nav pickCount={pickCount} user={user} isAdmin={isAdmin} />
      <Routes>
        {/* Public */}
        <Route path="/"              element={<Home user={user} />} />
        <Route path="/matches"       element={<Matches user={user} picks={picks} onPick={handlePick} />} />
        <Route path="/leaderboard"   element={<Leaderboard displayName={resolvedName} user={user} />} />
        <Route path="/theme-preview" element={<ThemeShowcase />} />

        {/* Auth page — redirect home if already signed in */}
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthPage />} />

        {/* Protected — redirect to /login if signed out */}
        <Route path="/my-picks" element={
          user ? <MyPicks user={user} />
               : <Navigate to="/login" replace />
        } />
        <Route path="/profile" element={
          user ? <Profile user={user} displayName={resolvedName} onDisplayNameChange={handleDisplayNameChange} />
               : <Navigate to="/login" replace />
        } />

        {/* Admin — only accessible to admin email, enforced in component + backend */}
        <Route path="/admin" element={<Admin user={user} />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <FeedbackButton />

      <footer className="app-footer">
        <p className="app-footer-tagline">TopQik — AI picks first. You pick better.</p>
        <p className="app-footer-powered">Predictions powered by Amazon Nova on AWS</p>
        <p className="app-footer-disclaimer">
          TopQik is an independent prediction platform and is not affiliated with, endorsed by,
          or connected to any football governing body. All competition and team names are used
          for identification purposes only. No real money is involved — points are for
          entertainment only.
        </p>
        <p className="app-footer-copy">© {new Date().getFullYear()} TopQik</p>
      </footer>
    </BrowserRouter>
  );
}
